import React, { useEffect, useMemo, useState } from 'react';

import { Box, Typography } from '@mui/material';

import { DialogUI, InputUI } from '../Interface';
import useParam from '../../context/ParamContext';
import projectMaterialService from '../../service/projectMaterial.service';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';

const highlightMatches = (text, query) => {
  const str = String(text ?? '');
  const rawQ = String(query || '').trim();
  if (!rawQ) return str;

  const tokens = rawQ.split(/\s+/).filter(Boolean);
  if (!tokens.length) return str;

  const lower = str.toLowerCase();
  let bestIndex = -1;
  let bestToken = '';

  tokens.forEach((token) => {
    const lowerToken = token.toLowerCase();
    const idx = lower.indexOf(lowerToken);
    if (idx !== -1 && (bestIndex === -1 || idx < bestIndex)) {
      bestIndex = idx;
      bestToken = token;
    }
  });

  if (bestIndex === -1) return str;

  const before = str.slice(0, bestIndex);
  const matchOriginal = str.slice(bestIndex, bestIndex + bestToken.length);
  const after = str.slice(bestIndex + bestToken.length);

  return [
    before,
    <span key="hl-0" style={{ color: '#0d47a1' }}>
      {matchOriginal}
    </span>,
    after,
  ];
};

const SearchDialog = ({ open, handleClose, searchQuery, setSelectedProject, onSearchChange }) => {
  const { params } = useParam();
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    if (!open) return;

    const query = String(searchQuery || '').trim();
    if (!query) {
      setProjects([]);
      return;
    }

    projectMaterialService.findProjects(query).then((data) => {
      setProjects(data || []);
    });
  }, [open, searchQuery]);

  const searchResults = useMemo(() => {
    const query = String(searchQuery || '')
      .trim()
      .toLowerCase();
    if (!query) return [];

    const tokens = query.split(/\s+/).filter(Boolean);
    if (!tokens.length) return [];

    return (projects || [])
      .filter((project) => project?.active !== false)
      .filter((project) => {
        const haystack = [project?.projectName, project?.orderNumber, project?.projectId, project?.id]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return tokens.every((token) => haystack.includes(token));
      })
      .slice(0, 100);
  }, [projects, searchQuery]);

  return (
    <DialogUI
      open={open}
      onClose={handleClose}
      headerContent={'Projekt kiválasztása'}
      sx={{
        '& .MuiDialog-container': { alignItems: 'flex-start' },
        '& .MuiPaper-root': { width: '100%', maxWidth: '800px', height: '70vh' },
        '& .MuiDialogContent-root': { overflowY: 'auto', maxHeight: '100%' },
        marginTop: '40px',
      }}
    >
      <Box my={2}>
        <InputUI
          type="search"
          placeholder="Keresés..."
          value={searchQuery}
          onChange={(e) => onSearchChange?.(e.target.value)}
          autoFocus
          fullWidth
          size="small"
          sx={{ flex: 1 }}
        />
      </Box>

      {searchResults.length === 0 ? (
        <Box>Nincs találat.</Box>
      ) : (
        <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {searchResults.map((project) => {
            const resolvedProjectId = project?.projectId ?? project?.id;

            return (
              <Box
                key={resolvedProjectId}
                onClick={() => {
                  setSelectedProject(project);
                  handleClose();
                }}
                sx={{
                  px: 1.5,
                  py: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                  {highlightMatches(project?.projectName ?? '', searchQuery)}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ display: 'block', mt: 0.25 }}
                >
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {getProjectOrderNumberName(params)}:
                  </Box>{' '}
                  {highlightMatches(project?.orderNumber ?? '', searchQuery)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </DialogUI>
  );
};

export default SearchDialog;
